import { useEffect, useRef } from "react";
import classnames from "classnames";
import { Link } from "react-router-dom";
import { ReactComponent as UGGLogo } from "@ugg/shared/assets/svg/logos/ugg-logo-blue.svg";
import { SvgGameVal } from "@ugitgud/legos/ui/icon/SVG/GameVal";
import { SvgGameLol } from "@ugitgud/legos/ui/icon/SVG/GameLol";
import { SvgGameDeadlock } from "@ugitgud/legos/ui/icon/SVG/GameDeadlock";
import { SvgBrandIcyVeins } from "@ugitgud/legos/ui/icon/SVG/BrandIcyVeins";
import { UGGLogoFull } from "@ugitgud/legos/ui/icon/UGGLogoFull";
import { SvgGameWowDetailed } from "@ugitgud/legos/ui/icon/SVG/GameWowDetailed";
import { NavTag } from "@ugitgud/legos/ui/form/tag/NavTag";
import { AppRoutes } from "@ugg/shared/routes/app-routes";
import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { SvgUggPlusOverlay } from "@ugitgud/legos/ui/icon/SVG/UggPlusOverlay";
import { SvgArrowLineRight } from "@ugitgud/legos/ui/icon/SVG/ArrowLineRight";
import { uggGAEvent } from "@ugitgud/legos/components/util/UggGAEventProvider";

function useVideoLoop(videoRef: HTMLVideoElement | null, loops: number) {
  const currLoops = useRef(1);

  useEffect(() => {
    function endedHandler() {
      if (videoRef && currLoops.current < loops) {
        videoRef.play();
        currLoops.current++;
      }
    }
    videoRef?.addEventListener("ended", endedHandler);

    return () => {
      videoRef?.removeEventListener("ended", endedHandler);
    };
  }, [videoRef, loops]);
}

export function WowPromo(props: { className?: string }) {
  return (
    <div className={classnames("relative flex items-center font-['Inter'] overflow-hidden", props.className)}>
      <div className="absolute flex flex-col left-0 top-0 bottom-0 ml-[18px] z-[2] pt-[24px] pb-[18px]">
        <div className="flex items-center text-[19px] font-semibold leading-[20px]">
          <img src="/images/ugg/games/WoW-detailed-icon.png" width={20} />
          <div className="flex item-center ml-[10px] h-[20px]">
            <UGGLogo className="w-[50px]" />
          </div>
          <span className="relative ml-[7px] top-[1px]">has WoW</span>
        </div>
        <div className="flex flex-col mt-[18px] mb-[20px] font-bold text-[24px] leading-[32px] text-white">
          Data Driven.
          <br />
          Always Best in Slot.
        </div>
        <a
          className={`
            flex items-center justify-center w-max mt-auto py-[10px] pl-[12px] pr-[8px] 
            rounded-[3px] font-bold text-[13px] leading-[16px] text-white 
            bg-accent-blue-400 hover:bg-accent-blue-600 cursor-pointer
          `}
          href="https://u.gg/wow/tier-list/dps-rankings/raid?utm_source=UGG&utm_campaign=BuildPage"
        >
          Check it Out
          <Icon icon={<SvgArrowLineRight />} className="ml-[6px] w-[16px] h-[16px]" />
        </a>
      </div>
      <video playsInline autoPlay loop muted className="h-full object-cover">
        <source type="video/webm" src="https://static.bigbrain.gg/assets/ugg/pages/champion/wowbgloop.webm" />
      </video>
      <div className="absolute top-0 w-full h-full z-[1] bg-[linear-gradient(270deg,rgba(25,25,55,0)_0%,#191937_100%)]" />
    </div>
  );
}

export function ValorantAppPromo(props: { className?: string }) {
  return (
    <div className={classnames("relative flex items-center font-['Inter'] overflow-hidden bg-purple-400", props.className)}>
      <div className="absolute flex flex-col left-0 top-0 bottom-0 ml-[18px] z-[2] pt-[24px] pb-[18px]">
        <div className="flex items-center text-[19px] font-semibold leading-[20px]">
          <SvgGameVal className="w-[20px]" />
          <div className="flex item-center ml-[10px] h-[20px]">
            <UGGLogo className="w-[50px]" />
          </div>
          <span className="relative ml-[7px] top-[1px]">Valorant App</span>
          <div className="relative top-[1px] flex items-center ml-[5px] px-[4px] h-[14px] rounded-[1px] bg-accent-forest-green-500 font-bold text-[10px]">
            BETA
          </div>
        </div>
        <div className="flex flex-col mt-[18px] mb-[20px] font-bold text-[24px] leading-[32px] text-white">
          Peek Maps,
          <br />
          Scouting & More
        </div>
        <a
          className={`
            flex items-center justify-center w-max mt-auto py-[10px] pl-[12px] pr-[8px] 
            rounded-[3px] font-bold text-[13px] leading-[16px] text-white 
            bg-accent-blue-400 hover:bg-accent-blue-600 cursor-pointer
          `}
          href="https://u.gg/val/app?utm_source=UGG&utm_campaign=BuildPage"
        >
          Download Now
          <Icon icon={<SvgArrowLineRight />} className="ml-[6px] w-[16px] h-[16px]" />
        </a>
      </div>
      <video playsInline autoPlay loop muted className="h-full object-cover ml-[73px]">
        <source type="video/webm" src="https://static.bigbrain.gg/assets/ugg/pages/champion/valorant-app-promo.webm" />
      </video>
      <div className="absolute top-0  left-[73px] w-full h-full z-[1] bg-[linear-gradient(270deg,rgba(25,25,55,0)_0%,#191937_100%)]" />
    </div>
  );
}

export function LoLAppPromo(props: { className?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  useVideoLoop(videoRef.current, 3);

  return (
    <div
      className={classnames(
        "relative flex items-center font-['Inter'] overflow-hidden bg-purple-400 min-h-[230px] max-h-[265px]",
        props.className,
      )}
    >
      <div className="absolute flex flex-col left-0 top-0 bottom-0 ml-[18px] z-[2] pt-[24px] pb-[18px]">
        <div className="flex items-center text-[19px] font-semibold leading-[20px]">
          <SvgGameLol className="w-[20px]" />
          <div className="flex item-center ml-[10px] h-[20px]">
            <UGGLogo className="w-[50px]" />
          </div>
          <span className="relative ml-[7px] top-[1px]">Desktop App</span>
        </div>
        <div className="flex flex-col mt-[18px] mb-[20px] font-bold text-[24px] leading-[32px] text-white">
          Never set runes again.
          <br />
          Auto-import runes,
          <br />
          items and skill order.
        </div>
        <Link
          className={`
            flex items-center justify-center w-max mt-auto py-[10px] pl-[12px] pr-[8px] 
            rounded-[3px] font-bold text-[13px] leading-[16px] text-white 
            bg-accent-red-500 hover:bg-accent-red-700 cursor-pointer
          `}
          to="/app?utm_source=UGG&utm_campaign=BuildPage"
        >
          Download App
          <Icon icon={<SvgArrowLineRight />} className="ml-[6px] w-[16px] h-[16px]" />
        </Link>
      </div>
      <video ref={videoRef} playsInline autoPlay muted className="h-full object-cover ml-[73px]">
        <source type="video/webm" src="https://static.bigbrain.gg/assets/ugg/pages/desktop-app/ugg-standalone-app.webm" />
      </video>
      <div className="absolute top-0  left-[73px] w-full h-full z-[1] bg-[linear-gradient(270deg,rgba(25,25,55,0)_0%,#191937_100%)]" />
    </div>
  );
}

export function DeadlockPromo(props: { className?: string }) {
  return (
    <div
      className={classnames(
        "relative flex items-center overflow-hidden bg-purple-400 min-h-[230px] rounded-br-[3px]",
        props.className,
      )}
    >
      <img
        className="absolute inset-0 h-full w-full object-cover object-right z-[0]"
        src="https://static.bigbrain.gg/assets/ugg/pages/champion/deadlock-promo.webp"
        alt=""
      />
      <div className="absolute inset-0 z-[1] bg-[linear-gradient(270deg,rgba(25,25,55,0)_45%,rgba(25,25,55,0.95)_100%)]" />
      <div className="relative flex flex-col self-stretch ml-[18px] z-[2] pt-[24px] pb-[18px]">
        <div className="flex items-center text-[19px] font-semibold leading-[20px]">
          <SvgGameDeadlock className="w-[20px]" />
          <div className="flex item-center ml-[10px] h-[20px]">
            <UGGLogo className="w-[50px]" />
          </div>
          <span className="relative ml-[7px] top-[1px]">Deadlock App</span>
          <NavTag shrink={false} size="sm" colorScheme="primary" className="ml-1.5 py-0.5 px-1 bg-accent-blue-400 rounded-sm">
            NEW
          </NavTag>
        </div>
        <div className="flex flex-col mt-[18px] mb-[20px] font-bold text-[24px] leading-[32px] text-white">
          Player Scouting,
          <br />
          In-game overlays
          <br />& more.
        </div>
        <Link
          className={`
            flex items-center justify-center w-max mt-auto py-[10px] pl-[12px] pr-[8px] 
            rounded-[3px] font-bold text-[13px] leading-[16px] text-white 
            bg-accent-red-500 hover:bg-accent-red-700 cursor-pointer
          `}
          to="/app?game=deadlock&utm_source=UGG&utm_campaign=BuildPage"
        >
          Download App
          <Icon icon={<SvgArrowLineRight />} className="ml-[6px] w-[16px] h-[16px]" />
        </Link>
      </div>
    </div>
  );
}

interface PlusPromoProps {
  className?: string;
  inNewWindow?: boolean;
}

const PLUS_EXTERNAL_URL = "https://u.gg/plus?utm_source=uggbuilds";

export function PlusPromo({ className, inNewWindow }: PlusPromoProps) {
  useEffect(() => {
    uggGAEvent("plus-builds-cta-view");
  }, []);

  const ctaClassName =
    "flex items-center justify-center w-full max-w-[150px] h-[36px] mt-auto rounded-[3px] font-semibold text-[13px] text-white bg-purple-500 hover:bg-purple-400 border-[1px] border-accent-yellow-400 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.4)] transition-all text-center block";

  const ctaContent = (
    <span className="relative">
      Start Your <span className="font-bold text-accent-yellow-400">Free</span> Trial
    </span>
  );

  const cta = inNewWindow ? (
    <a
      href={PLUS_EXTERNAL_URL}
      onClick={(e) => {
        e.preventDefault();
        window.open(PLUS_EXTERNAL_URL);
      }}
      className={ctaClassName}
    >
      {ctaContent}
    </a>
  ) : (
    <Link to={`${AppRoutes.PLUS}?utm_source=uggbuilds`} className={ctaClassName}>
      {ctaContent}
    </Link>
  );

  return (
    <div
      className={classnames(
        "relative flex items-center overflow-hidden min-h-[230px] rounded-br-[3px] border-b border-accent-yellow-400 bg-purple-500 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.4)]",
        className,
      )}
    >
      <img
        src="https://static.bigbrain.gg/assets/ugg/icons/senna-builds-page.webp"
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-[40%_center] z-[0]"
      />
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-[1] bg-purple-400 bg-opacity-50">
        <Icon icon={<SvgUggPlusOverlay />} className="w-[200%] -ml-[50%] h-auto min-h-full text-[#C8AC70] opacity-[0.15]" />
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(270deg, rgba(7, 7, 32, 0.8) 0%, rgba(7, 7, 32, 0) 50%, rgba(7, 7, 32, 0.8) 100%)",
          }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-[150%]"
          style={{
            background: "linear-gradient(0deg, rgba(50, 115, 250, 0.35) 0%, rgba(50, 115, 250, 0) 60%)",
          }}
        />
      </div>

      <div
        className="absolute inset-0 z-[2]"
        style={{
          background:
            "linear-gradient(180deg, rgba(7,7,32,0.2) 0%, rgba(7,7,32,0) 50%), linear-gradient(0deg, rgba(7,7,32,0.5) 0%, rgba(7,7,32,0) 50%)",
        }}
      />
      <img
        src="https://static.bigbrain.gg/assets/ugg/icons/discord-bot.png"
        alt=""
        className="absolute right-0 top-[44px] h-auto max-h-full w-auto z-[3] max-xs:max-h-[55%] max-xs:-right-[32px] sm:max-h-[55%] sm:-right-[32px] md:max-h-full md:right-0"
      />
      <div className="absolute flex flex-col left-0 top-0 bottom-0 ml-[24px] z-[3] pt-[28px] pb-[24px]">
        <div className="flex items-center text-[19px] font-semibold leading-[20px]">
          <UGGLogo className="w-[58.13px]" />
          <span className="ml-[8px] flex items-center justify-center h-[18px] px-[3.39px] rounded-[3.5px] text-[10.817px] font-semibold border border-accent-yellow-400 bg-accent-brown-400 text-accent-yellow-400">
            PLUS
          </span>
        </div>
        <div className="flex flex-col mt-[18px] mb-[20px] font-bold text-[24px] leading-[32px] text-white">
          Introducing
          <br />
          the brand new
          <br />
          Discord bot
        </div>
        {cta}
      </div>
    </div>
  );
}

export function WowAppPromo(props: { className?: string }) {
  return (
    <div
      className={classnames(
        "relative flex items-center overflow-hidden bg-[#191937] min-h-[230px] rounded-br-[3px]",
        props.className,
      )}
    >
      <img
        src="https://static.bigbrain.gg/assets/ugg/pages/desktop-app/wow/wow-build-cta.webp"
        alt="world of warcraft promo art"
        className="absolute inset-0 h-full w-full object-cover object-right z-[0]"
      />
      <div className="absolute inset-0 z-[1] bg-[linear-gradient(90deg,#060b20_35%,rgba(6,11,32,0)_100%)]" />
      <div className="absolute bottom-0 left-0 h-px w-full z-[2] bg-[#edda01]" />
      <div className="relative flex flex-col self-stretch p-4.5 z-[2]">
        <div className="flex items-center gap-[8px]">
          <Icon icon={<UGGLogoFull />} className="h-[20px] w-[62px]" />
          <span className="text-[#6b7a8f] text-[14px]">×</span>
          <SvgBrandIcyVeins className="h-[26px] w-[41px]" role="img" aria-label="Icy Veins" />
        </div>
        <div className="mt-[18px] mb-[12px] max-w-[248px] font-['Inter'] font-bold text-[24px] leading-[32px] text-white [text-shadow:0px_2px_4px_rgba(0,0,0,0.25)]">
          The Icy Veins WoW App is Here!
        </div>
        <div className="mb-[20px] max-w-[231px] font-barlow font-medium text-[19px] leading-normal text-white">
          Class Guides, Tier Lists, Class Codex &amp; More
        </div>
        <Link
          className={`
            flex items-center justify-center gap-[10px] w-[150px] h-[36px] px-[12px] py-[4px] mt-auto
            rounded-[3px] bg-[#0b7ba4] border border-[#ade9ff] shadow-[0px_2px_2px_0px_rgba(0,0,0,0.25)]
            drop-shadow-[0px_2px_0.5px_rgba(0,0,0,0.4)]
            font-barlow-condensed font-semibold text-[16px] leading-[16px] text-white
            cursor-pointer hover:bg-[#0d93c4]
          `}
          to="/app?game=wow&utm_source=UGG&utm_campaign=BuildPage"
        >
          <Icon icon={<SvgGameWowDetailed />} className="w-[20px] h-[20px]" />
          <span>DOWNLOAD NOW</span>
        </Link>
      </div>
    </div>
  );
}
